/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Backdrop,
  CircularProgress,
  Snackbar,
  Breadcrumbs,
  Link,
  Container,
  Switch
} from "@mui/material";
import { fetchSubmissionDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import "./EditRecurringProduct.css";
import Topheader from "../../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ApplicationDetailsErrorDialog from "../../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { useForm } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import APIManager from "../../../Services/ApiManager/ApiManager";
import Alert from "@mui/material/Alert";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import PropTypes from "prop-types";

// LoadingBackdrop Component - Reusable loading indicator
const LoadingBackdrop = ({ open }) => (
  <Backdrop
    sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
    open={open}
  >
    <CircularProgress color="inherit" />
  </Backdrop>
);

LoadingBackdrop.propTypes = {
  open: PropTypes.bool.isRequired,
};

function createData(
  subscriptionFrequency,
  subscriptionFrequencyUnit,
  price,
  currencyCode,
  planId,
  productId
) {
  return {
    subscriptionFrequency,
    subscriptionFrequencyUnit,
    price,
    currencyCode,
    planId,
    productId,
  };
}

function EditRecurringProduct(props) {
  const { appId, productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const teamId = location.state.teamId;
  const sellerId = location.state.sellerId;
  const [product, setProduct] = useState([]);
  const [plans, setPlans] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const { loading, submissionDetails } = useSelector(
    (state) => state.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "onChange",
  });
  const frequencyUnitConverter = {
    "1-DAY": "Daily",
    "1-MONTH": "Monthly",
    "3-MONTH": "Quarterly",
    "6-MONTH": "Half yearly",
    "1-YEAR": "Yearly",
  };

  const errorsApp = useSelector((state) => state.errors);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(true);
  const [usedRecurringFrequencies, setUsedRecurringFrequencies] = useState([]);

  // Helper function to render form field with label and optional error
  const renderFormField = (label, registerName, placeholder, error, type = "text", multiline = false) => (
    <div>
      <label className="form-label" htmlFor={`${registerName}-input`}>{label}</label>
      <TextField
        {...register(registerName)}
        id={`${registerName}-input`}
        rows={multiline ? 4 : undefined}
        multiline={multiline}
        type={type}
        style={{ marginBottom: "0", marginLeft: "0" }}
        fullWidth
        placeholder={placeholder}
        variant="outlined"
      />
      {error && <Alert severity="error">{error.message}</Alert>}
    </div>
  );

  useEffect(() => {
    if (productId && productId !== null) {
      APIManager.getRecurringAppDetails(productId).then((json) => {
        setProduct(json);
        setValue("productId", json.product.productId);
        setValue("name", json.product.name);
        setValue("description", json.product.description);
        setValue("trialPeriod", json.trialPrice?.trialPeriod);
        if (json.recurringPlans && json.recurringPlans.length > 0) {
          let allRows = [];
          let tempArr = [];
          json.recurringPlans.forEach((plan) => {
            tempArr.push(
              plan.recurringFrequency + "-" + plan.recurringFrequencyUnit
            );
            allRows.push(
              createData(
                plan.recurringFrequency,
                plan.recurringFrequencyUnit,
                plan.basePrice.price,
                plan.basePrice.currencyCode,
                plan.id,
                json.product.id
              )
            );
          });
          setPlans(allRows);
          setUsedRecurringFrequencies(tempArr);
        }
        setLoader(false);
      });
    }
  }, []);

  const backToApplications = () => {
    navigate(-1);
  };

  const backToAppDetails = () => {
    navigate(`/ui/developer/applications/${appId}`, {
      state: { teamId: teamId, sellerId: sellerId },
    });
  };

  const callNewSubmissionId = (submissionId) => {
    dispatch(fetchSubmissionDetails(appId, submissionId));
  };

  const onSubmit = (data) => {
    setLoader(true);
    let productToSave = product;
    productToSave.product.name = data.name;
    productToSave.product.description = data.description;
    if (data.trialPeriod && data.trialPeriod !== 0) {
      if (productToSave.trialPrice == null) {
        productToSave.trialPrice = {
          status: "ACTIVE",
          trialPeriod: data.trialPeriod,
          trialPeriodUnit: "DAY",
          trialFlag: true,
          name: data.productId,
          description: data.productId + "_trial",
        };
      } else {
        productToSave.trialPrice.trialPeriod = data.trialPeriod;
      }
    } else {
      productToSave.trialPrice = null;
    }
    saveProduct(productToSave);
  };

  const saveProduct = (data) => {
    APIManager.updateInAppRecurrence(data, productId).then(() => {
      setOpenSnackbar(true);
      setSnackMsg(`Updated successfully`);
      setTimeout(function () {
        navigate(-1);
      }, 3000);
    });
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.action.hover,
      fontWeight: "bold",
      border: 0,
      padding: '4px 8px',
      height: '32px',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      padding: '4px 8px',
      height: '34px',
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
    height: '34px',
    '& .MuiTableCell-root': {
      padding: '4px 8px',
      height: '34px',
    },
  }));

  const goToPlan = () => {
    navigate(`/ui/developer/applications/${appId}/plan/${productId}`, {
      state: {
        teamId: teamId,
        sellerId: sellerId,
        product: product,
        usedRecurringFrequencies: usedRecurringFrequencies,
      },
    });
  };

  const goToEditPlan = (planId) => {
    navigate(
      `/ui/developer/applications/${appId}/plan/${productId}/edit/${planId}`,
      {
        state: {
          teamId: teamId,
          sellerId: sellerId,
          product: product,
          usedRecurringFrequencies: usedRecurringFrequencies,
        },
      }
    );
  };

  return (
    <>
      {loader && <LoadingBackdrop open={loader} />}

      <Topheader />
      {
        <Container maxWidth="lg">
          <Grid container>
            <Grid items xs={12} sm={12} md={12} lg={12}></Grid>
            <div className="container" style={submissionDetails.status === "DRAFT" ? { display: "flex", justifyContent: "center", flexDirection: "column", width: "1260px" } : { display: "flex", flexDirection: "row", width: "1260px" }}>
              <div className="side-menu" style={submissionDetails.status === "DRAFT" ? { marginTop: "0px" } : { marginTop: "69px" }}>
                <button className="arrow-back" onClick={backToApplications} type="button" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                  <div><ArrowBackIcon style={{ color: " #717273" }} /></div>
                  <div className="my-apps">In App Products</div>
                </button>
                {submissionDetails && Object.keys(submissionDetails).length > 0 && submissionDetails.status !== "DRAFT" && appData && Object.keys(appData).length > 0 && (
                  <ApplicationDetailsSideMenu appData={appData} teamId={teamId} isMonetized={submissionDetails.isMonetized} callNewSubmissionId={callNewSubmissionId} />
                )}
              </div>
              <div className="submission-container" style={submissionDetails.status === "DRAFT" ? { marginLeft: "0" } : { width: "93%", marginTop: "12px", marginLeft: "70px" }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="in-app-title" style={{ marginLeft: "15%" }}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    onClick={backToAppDetails}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowBackIcon
                      sx={{ mr: 0.5, marginRight: "19px", color: "#b3b3b3" }}
                      fontSize="inherit"
                    />
                    {submissionDetails &&
                      Object.keys(submissionDetails).length > 0 && (
                        <>{submissionDetails.descriptions[0].applicationName}</>
                      )}
                  </Link>
                  <Link
                    underline="hover"
                    onClick={backToApplications}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    In App Products
                  </Link>
                  <Link
                    underline="none"
                    sx={{ fontWeight: "500", color: "#2a2c2d" }}
                  >
                    Edit Product
                  </Link>
                </Breadcrumbs>
                <Grid container spacing={2}>
                  <Grid size={9}>
                    <h2>Edit Product</h2>
                  </Grid>
                  <Grid size={3}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        sx={(theme) => ({
                          backgroundColor: "#e8e8e8",
                          color: "black",
                          textTransform: "none",
                        })}
                        onClick={backToApplications}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        sx={{ textTransform: "none" }}
                        disabled={!isValid}
                        type="submit"
                      >
                        Save
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </div>
              <div className="products-container">
                <Grid container spacing={2}>
                  <Grid size={4}>Basic Information</Grid>
                  <Grid size={4}>
                    <div>
                      <label className="form-label required" htmlFor="productId-input">Product ID</label>
                      <TextField
                        {...register("productId", {
                          required: "Product Id is required",
                        })}
                        id="productId-input"
                        disabled
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        placeholder="Product ID"
                        variant="outlined"
                      />

                      {errors.productId && (
                        <Alert severity="error">
                          {errors.productId.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                  <Grid size={4}>
                    <div>
                      <label className="form-label required" htmlFor="name-input">Name</label>
                      <TextField
                        {...register("name", {
                          required: "Name is required",
                        })}
                        id="name-input"
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        placeholder="Name"
                        variant="outlined"
                      />
                      {errors.name && (
                        <Alert severity="error">{errors.name.message}</Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={4}></Grid>
                  <Grid size={8}>
                    <div>
                      <label className="form-label required" htmlFor="description-input">Description</label>
                      <TextField
                        {...register("description", {
                          required: "Description is required",
                        })}
                        id="description-input"
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        placeholder="Description"
                        variant="outlined"
                      />
                      {errors.description && (
                        <Alert severity="error">
                          {errors.description.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={4}></Grid>
                  <Grid size={3}>
                    {renderFormField("Trial Period", "trialPeriod", "Trial Period", null, "text", true)}
                  </Grid>
                  <Grid size={1.5}>
                    <div>
                      <label className="form-label no-label" htmlFor="trial-period-unit-select" aria-label="Trial period unit"> </label>
                      <Select fullWidth value="" displayEmpty inputProps={{ "aria-label": "Without label" }} id="trial-period-unit-select">
                        <MenuItem value="">Days</MenuItem>
                      </Select>
                    </div>
                  </Grid>
                </Grid>
              </div>
              <div className="products-container-wide">
                <Divider flexItem />
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}>Recurrence & Price</Grid>
                  <Grid size={8.5}>
                    <div className="monetize-wrapper">
                      <div className="wrapper-diver">
                        <p
                          className="monetize-text"
                          style={{ fontWeight: "bold" }}
                        >
                          Enable Recurrence{" "}
                          <span style={{ color: "red" }}>*</span>
                          <br />
                          <span
                            className="monetize-text-small"
                            style={{ marginLeft: "0" }}
                          >
                            <b>Note: </b>
                            <span style={{ color: "#757575" }}>
                              This setting cannot be undone once this product is
                              published
                            </span>
                          </span>
                        </p>

                        <div>
                          <Switch
                            {...register("enableRecurrence", {})}
                            disabled
                            checked
                          />
                        </div>
                      </div>
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={8.5}>
                    <br />
                    <h4>Plans</h4>
                    {plans.length === 0 && (
                      <p
                        className="monetize-text-small"
                        style={{
                          marginLeft: "0%",
                          color: "#757575",
                          marginTop: "-3%",
                        }}
                      >
                        You can set multiple plans
                      </p>
                    )}
                    {plans.length > 0 && (
                      <TableContainer>
                        <Table aria-label="customized table">
                          <TableHead>
                            <TableRow>
                              <StyledTableCell>
                                Subscription Frequency
                              </StyledTableCell>
                              <StyledTableCell>Price</StyledTableCell>
                              <StyledTableCell>Offer</StyledTableCell>
                              <StyledTableCell></StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {plans.map((plan) => (
                              <StyledTableRow key={plan.planId}>
                                <StyledTableCell sx={{ fontWeight: "bold" }}>
                                  {
                                    frequencyUnitConverter[
                                      plan.subscriptionFrequency +
                                        "-" +
                                        plan.subscriptionFrequencyUnit
                                    ]
                                  }
                                </StyledTableCell>
                                <StyledTableCell>{plan.price}</StyledTableCell>
                                <StyledTableCell>0 Offers</StyledTableCell>
                                <StyledTableCell>
                                  <EditIcon
                                    className="edit-icon"
                                    onClick={() => goToEditPlan(plan.planId)}
                                  />
                                </StyledTableCell>
                              </StyledTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {plans.length < 4 && (
                      <Button
                        sx={{ marginTop: "1%", textTransform: "none" }}
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={goToPlan}
                      >
                        Add Plan
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </div>
            </form>
              </div>
            </div>
          </Grid>
        </Container>
      }
      {errorsApp?.errorMessage && errorsApp?.errorType && <ApplicationDetailsErrorDialog />}
      {(appData.loading || loading) && <LoadingBackdrop open={loading} />}
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)} message={snackMsg} />
    </>
  );
}

export default EditRecurringProduct;
